import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuditTrailModal from './modals/AuditTrailModal';
import { ReportsProvider, useReports } from './ReportsProvider';
import { REPORT_TABS, REPORT_CATEGORIES, REPORT_TYPES } from './utils/reportConstants';
import { reportValidation } from './utils/reportValidation';
import axios from 'axios';
import { API } from '../../utils/api';
import { useSubscriptionFeatures } from '../../utils/useSubscriptionFeatures';

// Import modal components
import WorkDiaryPhotosModal from './tabs/modals/WorkDiaryPhotosModal';
import UploadPhotosModal from './tabs/modals/UploadPhotosModal';
import ReportGenerationModal from './modals/ReportGenerationModal';
import AIReportGeneratorModal from './modals/AIReportGeneratorModal';
import ConfirmModal from '../modals/ConfirmModal';
import SuccessModal from '../modals/SuccessModal';
import ErrorModal from '../modals/ErrorModal';

// Import shared toolbar
import SharedRichTextToolbar from './components/SharedRichTextToolbar';
import { SectionIncludeSwitch, SectionPresetBar, ExcludedSectionBanner } from './components/SectionInclusionControls';

// Import tab components
import CoverPageTab from './tabs/CoverPageTab';
import ContentsTab from './tabs/ContentsTab';
import PhotoSelectionTab from './tabs/PhotoSelectionTab';
import { 
  ExecutiveSummaryTab,
  ProjectOverviewTab, 
  ProgressHighlightsTab, 
  WorkCompletedTab, 
  QualityInspectionsTab, 
  SafetyComplianceTab,
  SchedulePerformanceTab,
  ResourceUtilizationTab,
  BudgetAnalysisTab,
  RiskAssessmentTab,
  ProgressComparisonsTab,
  TechnicalDrawingsTab,
  NextPhaseTab,
  AppendicesTab
} from './tabs/OtherReportTabs';

const ReportsContent = () => {
  const { 
    activeReportType,
    setActiveReportType,
    activeTab, 
    setActiveTab,
    hasUnsavedChanges,
    saveReportWithPhotos,
    populateReportWithSampleData,
    isGenerating,
    getTabCompletionStatus,
    generateFullReport,
    checkRequiredFields,
    loadPhotosForCurrentPeriod,
    addUserUploadedPhotos,
    reportData,
    setReportData,
    selectedPeriod,
    setSelectedPeriod,
    selectedSite,
    setSelectedSite,
    sites,
    // Photo management state
    importedPhotos,
    photoLabels,
    setImportedPhotos,
    setSelectedPhotos,
    setPhotoLabels,
    setAvailablePhotos,
    // Report sections state
    reportSections,
    setReportSections,
    isSectionIncluded
  } = useReports();

  const { subscriptionFeatures } = useSubscriptionFeatures();
  const [tooltipPos, setTooltipPos] = useState({left: 0, top: 0});

  // State for forcing status indicator re-calculation
  const [statusUpdateTrigger, setStatusUpdateTrigger] = useState(0);
  
  // State for audit trail modal
  const [auditTrailModal, setAuditTrailModal] = useState({ isOpen: false, reportId: null, reportTitle: '' });
  
  // Navigation blocker state - lifted to component level
  const [hasUnsavedChangesForNav, setHasUnsavedChangesForNav] = useState(false);
  const [showNavigationBlocker, setShowNavigationBlocker] = useState(false);
  const pendingNavigationRef = useRef(null);
  const location = useLocation();
  const navigateForBlocker = useNavigate();
  
  // Expose unsaved changes state globally for AdminLayout to check
  useEffect(() => {
    window.__REPORTS_UNSAVED_CHANGES__ = hasUnsavedChangesForNav;
    window.__REPORTS_SHOW_NAV_BLOCKER__ = (targetPath) => {
      pendingNavigationRef.current = targetPath;
      setShowNavigationBlocker(true);
    };
    
    return () => {
      window.__REPORTS_UNSAVED_CHANGES__ = false;
      window.__REPORTS_SHOW_NAV_BLOCKER__ = null;
    };
  }, [hasUnsavedChangesForNav]);
  
  // Create a serialized version of reportSections for deep comparison
  const reportSectionsHash = useMemo(() => {
    try {
      return JSON.stringify(reportSections);
    } catch (e) {
      return Date.now().toString(); // Fallback
    }
  }, [reportSections]);
  
  // Create a serialized version of importedPhotos for deep comparison
  const importedPhotosHash = useMemo(() => {
    try {
      return JSON.stringify(importedPhotos);
    } catch (e) {
      return Date.now().toString(); // Fallback
    }
  }, [importedPhotos]);
  
  // Create a serialized version of photoLabels for deep comparison
  const photoLabelsHash = useMemo(() => {
    try {
      return JSON.stringify(photoLabels);
    } catch (e) {
      return Date.now().toString(); // Fallback
    }
  }, [photoLabels]);
  
  // Force status indicator re-calculation when reportSections OR importedPhotos change
  useEffect(() => {
    console.log('🔄 reportSections or importedPhotos changed, incrementing statusUpdateTrigger');
    setStatusUpdateTrigger(prev => {
      const newValue = prev + 1;
      console.log(`   Trigger: ${prev} → ${newValue}`);
      // Force a visible side effect to verify this is running
      if (typeof window !== 'undefined') {
        window.__STATUS_UPDATE_COUNT__ = newValue;
      }
      return newValue;
    });
  }, [reportSectionsHash, importedPhotosHash]);

  // Get completion indicator
  const getCompletionIndicator = (status) => {
    const indicator = reportValidation.getCompletionIndicator(status);
    return <div className={indicator.className}></div>;
  };

  // Handle photo upload for the main page buttons
  const handlePhotoUpload = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Convert FileList to Array and process each file
    const newPhotos = Array.from(files).map(file => {
      // Create object URL for preview
      const url = URL.createObjectURL(file);
      
      return {
        id: `imported_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        url: url,
        file: file, // Keep reference to original file
        size: file.size,
        type: 'user-uploaded',
        imported: true,
        created_at: new Date().toISOString()
      };
    });

    // Add the photos to imported photos using the hook function
    addUserUploadedPhotos(newPhotos);

    // Clear the input so the same file can be selected again if needed
    event.target.value = '';
    
    console.log(`Successfully imported ${newPhotos.length} photo(s)`);
  };

  // Render main report type selection page
  const renderReportTypeSelection = () => {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">📊 Analytics & Reports</h2>
          <p className="text-gray-600">
            Generate comprehensive reports for construction progress, work diary, attendance, and financial analysis.
          </p>
        </div>

        {/* Report Type Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {REPORT_TYPES.map(reportType => (
            <div
              key={reportType.id}
              onClick={() => setActiveReportType(reportType.id)}
              className={`bg-white rounded-xl border-2 p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-${reportType.color}-300 ${
                activeReportType === reportType.id 
                  ? `border-${reportType.color}-500 bg-${reportType.color}-50` 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-lg bg-${reportType.color}-100`}>
                  <span className="text-2xl">{reportType.icon}</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{reportType.name}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{reportType.description}</p>
                  <div className="mt-4">
                    <button className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-200 bg-${reportType.color}-600 text-white hover:bg-${reportType.color}-700`}>
                      Generate Report
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render progress reports interface (existing functionality)
  const renderProgressReportsTabInterface = () => {
    // State for collapsible saved reports section
    const [savedReportsExpanded, setSavedReportsExpanded] = useState(false);
    const [selectedTimePeriod, setSelectedTimePeriod] = useState('This Month');
    const [currentEditingReportId, setCurrentEditingReportId] = useState(null); // Track which report is being edited
    const [allSavedReports, setAllSavedReports] = useState([]);
    const [isLoadingReports, setIsLoadingReports] = useState(false);
    const [downloadingReportId, setDownloadingReportId] = useState(null); // Track which report is being downloaded
    
    // Unsaved changes tracking
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [baselineFormState, setBaselineFormState] = useState({
      reportData: { title: '', description: '' },
      selectedSite: '',
      selectedPeriod: { startDate: '', endDate: '' },
      reportSectionsHash: '',
      importedPhotosHash: '',
      photoLabelsHash: ''
    });
    
    // Modal states for photo selection and report generation
    const [showWorkDiaryModal, setShowWorkDiaryModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showReportGenerationModal, setShowReportGenerationModal] = useState(false);
    const [showAIReportModal, setShowAIReportModal] = useState(false);
    
    // Custom modal states for replacing browser dialogs
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    const [successModal, setSuccessModal] = useState({ isOpen: false, title: '', message: '' });
    const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });
    
    // Shared toolbar state for rich text editing
    const [isRichTextToolbarActive, setIsRichTextToolbarActive] = useState(false);
    const [focusedTextboxInfo, setFocusedTextboxInfo] = useState(null);
    const [hasGridFocus, setHasGridFocus] = useState(false);
    const [hasTableFocus, setHasTableFocus] = useState(false);
    const [isCustomTableFocused, setIsCustomTableFocused] = useState(false);
    const toolbarCommandRef = useRef(null);
    const textboxActionsRef = useRef({ onAdd: null, onDelete: null });
    
    // Get current year for dropdown options
    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 1;
    
    // Mock current user (replace with actual user data)
    const currentUser = {
      id: 'current-user',
      name: 'Current User',
      role: 'admin'
    };
    
    // Helper function to check for unsaved changes
    const checkForUnsavedChanges = () => {
      const hasBasicChanges = (
        (reportData.title || '') !== baselineFormState.reportData.title ||
        (reportData.description || '') !== baselineFormState.reportData.description ||
        (selectedSite || '') !== baselineFormState.selectedSite ||
        (selectedPeriod.startDate || '') !== baselineFormState.selectedPeriod.startDate ||
        (selectedPeriod.endDate || '') !== baselineFormState.selectedPeriod.endDate
      );
      
      const hasContentChanges = (
        reportSectionsHash !== baselineFormState.reportSectionsHash ||
        importedPhotosHash !== baselineFormState.importedPhotosHash ||
        photoLabelsHash !== baselineFormState.photoLabelsHash
      );
      
      const hasChanges = hasBasicChanges || hasContentChanges;
      
      setHasUnsavedChanges(hasChanges);
      setHasUnsavedChangesForNav(hasChanges); // Sync with navigation blocker
      return hasChanges;
    };
    
    // Helper function to update baseline state (call after save or load)
    const updateBaselineState = () => {
      const newBaseline = {
        reportData: { title: reportData.title || '', description: reportData.description || '' },
        selectedSite: selectedSite || '',
        selectedPeriod: { 
          startDate: selectedPeriod.startDate || '', 
          endDate: selectedPeriod.endDate || '' 
        },
        reportSectionsHash: reportSectionsHash,
        importedPhotosHash: importedPhotosHash,
        photoLabelsHash: photoLabelsHash
      };
      setBaselineFormState(newBaseline);
      setHasUnsavedChanges(false);
      setHasUnsavedChangesForNav(false); // Sync with navigation blocker
    };
    
    // Helper function to prompt user about unsaved changes
    const promptForUnsavedChanges = (onConfirm) => {
      if (hasUnsavedChanges) {
        setConfirmModal({
          isOpen: true,
          title: 'Unsaved Changes',
          message: 'You have unsaved changes that will be lost. Do you want to continue without saving?',
          onConfirm: () => {
            setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
            onConfirm();
          }
        });
        return false; // Indicate that async confirmation is needed
      }
      return true; // No unsaved changes, proceed immediately
    };
    
    // Load saved reports from backend
    useEffect(() => {
      loadSavedReportsFromBackend();
    }, [selectedTimePeriod]);
    
    // Track changes to form state for unsaved changes detection
    useEffect(() => {
      checkForUnsavedChanges();
    }, [reportData.title, reportData.description, selectedSite, selectedPeriod.startDate, selectedPeriod.endDate, reportSectionsHash, importedPhotosHash, photoLabelsHash]);

    // Re-baseline after a loaded report's state has been committed (avoids a stale baseline)
    const [baselineRequest, setBaselineRequest] = useState(0);
    useEffect(() => {
      if (baselineRequest > 0) updateBaselineState();
    }, [baselineRequest]);
    
    // Listen for progress comparisons state changes
    useEffect(() => {
      const handleStateChange = (event) => {
        console.log('Progress comparisons state change event received:', event.detail);
        setHasGridFocus(event.detail.hasFocusedGrid);
        console.log('hasGridFocus updated to:', event.detail.hasFocusedGrid);
      };
      
      window.addEventListener('progressComparisonsStateChange', handleStateChange);
      
      return () => {
        window.removeEventListener('progressComparisonsStateChange', handleStateChange);
      };
    }, []);

    // Listen for technical drawings state changes
    useEffect(() => {
      const handleTechnicalStateChange = (event) => {
        console.log('Technical drawings state change event received:', event.detail);
        setHasTableFocus(event.detail.hasTableFocus);
        setIsCustomTableFocused(event.detail.isCustomTable);
        console.log('hasTableFocus updated to:', event.detail.hasTableFocus);
        console.log('isCustomTableFocused updated to:', event.detail.isCustomTable);
      };
      
      window.addEventListener('technicalDrawingsStateChange', handleTechnicalStateChange);
      
      return () => {
        window.removeEventListener('technicalDrawingsStateChange', handleTechnicalStateChange);
      };
    }, []);

    // Warn user before leaving page if there are unsaved changes
    useEffect(() => {
      const handleBeforeUnload = (e) => {
        if (hasUnsavedChanges) {
          e.preventDefault();
          e.returnValue = ''; // Required for Chrome
          return ''; // Required for some browsers
        }
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }, [hasUnsavedChanges]);
    
    const loadSavedReportsFromBackend = async () => {
      setIsLoadingReports(true);
      try {
        const response = await axios.get(`${API}/admin/saved-reports`, {
          params: { period: selectedTimePeriod },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        setAllSavedReports(response.data);
        console.log(`Loaded ${response.data.length} saved reports for ${selectedTimePeriod}`);
      } catch (error) {
        console.error('Error loading saved reports:', error);
        // Keep existing data on error, don't clear it
      } finally {
        setIsLoadingReports(false);
      }
    };
    
    // Generate unique ID for new reports
    const generateReportId = () => {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substr(2, 5);
      return `RPT-${currentYear}-${random.toUpperCase()}`;
    };
    
    // Helper function to get site name from site ID using actual sites data
    const getSiteName = (siteId) => {
      if (!siteId || !sites || sites.length === 0) {
        return 'Unknown Site';
      }
      
      const site = sites.find(site => site.id === siteId);
      return site ? site.site_name : 'Unknown Site';
    };
    
    // Helper function to format author name as "FirstName L"
    const formatAuthorName = (authorName) => {
      if (!authorName) return 'Unknown';
      
      // Split the full name
      const nameParts = authorName.trim().split(' ');
      if (nameParts.length === 0) return 'Unknown';
      
      // Get first name
      const firstName = nameParts[0];
      
      // Get last name initial if available
      if (nameParts.length > 1) {
        const lastNameInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
        return `${firstName} ${lastNameInitial}`;
      }
      
      // If only one name part, return as is
      return firstName;
    };
    
    // Get filtered reports (now all filtering is done on backend)
    const savedReports = allSavedReports;
    
    // Save current report functionality - Enhanced to capture ALL fields and photos
    const handleSaveCurrentReport = async () => {
      if (!reportData.title || !selectedSite || !selectedPeriod.startDate || !selectedPeriod.endDate) {
        setErrorModal({
          isOpen: true,
          title: 'Incomplete Report',
          message: 'Please complete the Cover Page & Project Info tab before saving the report.'
        });
        return;
      }
      
      // Check for duplicate title (excluding current report if editing)
      const duplicateReport = allSavedReports.find(report => 
        report.title.toLowerCase().trim() === reportData.title.toLowerCase().trim() &&
        report.id !== currentEditingReportId
      );
      
      if (duplicateReport) {
        setErrorModal({
          isOpen: true,
          title: 'Duplicate Title',
          message: `A report with the title "${reportData.title}" already exists.\n\nPlease choose a different title for your report.`
        });
        return;
      }
      
      const siteName = getSiteName(selectedSite);
      
      try {
        // Get comprehensive report state including ALL sections and photos
        const { reportUtils } = require('./utils/reportUtils.js');
        const comprehensiveReportState = reportUtils.getCurrentReportState(
          reportData,
          reportSections || {},
          selectedPeriod,
          selectedSite,
          importedPhotos || [],
          photoLabels || {}
        );
        
        if (currentEditingReportId) {
          // Convert photoLabels from {label, description} objects to simple strings for backend
          const photoLabelsForBackend = {};
          if (photoLabels) {
            Object.keys(photoLabels).forEach(photoId => {
              const labelData = photoLabels[photoId];
              // If it's an object with label/description, extract just the label string
              photoLabelsForBackend[photoId] = typeof labelData === 'object' ? 
                (labelData.label || labelData.description || '') : 
                (labelData || '');
            });
          }
          
          // Update existing report with ALL data
          const updateData = {
            title: reportData.title,
            site: selectedSite,  // Use site ID, not name
            reportData: {
              title: reportData.title,
              description: reportData.description || '',
              selectedSite: selectedSite,
              selectedPeriod: selectedPeriod,
              // Include ALL report sections
              reportSections: reportSections || {},
              // Include ALL photo data
              importedPhotos: importedPhotos || [],
              photoLabels: photoLabelsForBackend,  // Use converted labels
              // Store complete state for reconstruction
              comprehensiveState: comprehensiveReportState
            }
          };
          
          const response = await axios.put(`${API}/admin/saved-reports/${currentEditingReportId}`, updateData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          // Update local state
          setAllSavedReports(prevReports => 
            prevReports.map(report => 
              report.id === currentEditingReportId ? response.data : report
            )
          );
          
          setSuccessModal({
            isOpen: true,
            title: 'Report Updated',
            message: `Report updated successfully!\n\nReport ID: ${currentEditingReportId}\nSite: ${siteName}\nLast Saved: ${new Date(response.data.lastSaved).toLocaleString()}\n\n✅ All fields, sections, and ${(importedPhotos || []).length} photos have been saved.`
          });
          console.log(`Updated existing report: ${currentEditingReportId} with comprehensive data`);
        } else {
          // Create new report with ALL data
          const newReportData = {
            title: reportData.title,
            site: selectedSite,  // Use site ID, not name
            reportData: {
              title: reportData.title,
              description: reportData.description || '',
              selectedSite: selectedSite,
              selectedPeriod: selectedPeriod,
              // Include ALL report sections
              reportSections: reportSections || {},
              // Include ALL photo data
              importedPhotos: importedPhotos || [],
              photoLabels: photoLabels || {},
              // Store complete state for reconstruction
              comprehensiveState: comprehensiveReportState
            }
          };
          
          // Convert photoLabels from {label, description} objects to simple strings for backend
          const photoLabelsForBackend = {};
          if (photoLabels) {
            Object.keys(photoLabels).forEach(photoId => {
              const labelData = photoLabels[photoId];
              // If it's an object with label/description, extract just the label string
              photoLabelsForBackend[photoId] = typeof labelData === 'object' ? 
                (labelData.label || labelData.description || '') : 
                (labelData || '');
            });
          }
          
          // Update the reportData with converted photoLabels
          newReportData.reportData.photoLabels = photoLabelsForBackend;
          
          console.log('📤 About to save new report. Data preview:', {
            title: newReportData.title,
            site: newReportData.site,
            siteType: typeof newReportData.site,
            selectedSite: selectedSite,
            reportDataKeys: Object.keys(newReportData.reportData),
            photoLabelsCount: Object.keys(photoLabelsForBackend).length
          });
          
          const response = await axios.post(`${API}/admin/saved-reports`, newReportData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          // Update local state
          setAllSavedReports(prevReports => [response.data, ...prevReports]);
          
          // CRITICAL: Set the report to edit mode after successful creation
          setCurrentEditingReportId(response.data.id);
          
          setSuccessModal({
            isOpen: true,
            title: 'Report Created',
            message: `New report created successfully!\n\nReport ID: ${response.data.id}\nSite: ${siteName}\n\n✅ All fields, sections, and ${(importedPhotos || []).length} photos have been saved.\n\nThe report is now loaded for editing. Future saves will update this report.`
          });
          console.log(`Created new report: ${response.data.id} with comprehensive data - switched to edit mode`);
        }
        
        // Clear unsaved changes flag after successful save
        updateBaselineState();
        
      } catch (error) {
        console.error('Error saving comprehensive report:', error);
        console.error('Error details:', {
          response: error.response?.data,
          status: error.response?.status,
          message: error.message
        });
        
        let errorMessage = 'Unknown error occurred';
        if (error.response?.data?.detail) {
          // FastAPI validation error
          if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail.map(err => 
              `${err.loc?.join('.')}}: ${err.msg}`
            ).join('\n');
          } else {
            errorMessage = error.response.data.detail;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setErrorModal({
          isOpen: true,
          title: 'Error Saving Report',
          message: `Error saving report:\n\n${errorMessage}\n\nPlease check your connection and try again.`
        });
      }
    };
    
    // Edit report functionality - Enhanced to load ALL saved data
    const handleEditReport = (report) => {
      // Check for unsaved changes before loading different report
      const canProceed = promptForUnsavedChanges(() => {
        loadReportData(report);
      });
      
      if (canProceed) {
        // No unsaved changes, proceed immediately
        loadReportData(report);
      }
    };
    
    const loadReportData = (report) => {
      console.log(`Loading comprehensive report for editing: ${report.id}`);
      
      // Set the current editing report ID
      setCurrentEditingReportId(report.id);
      
      // Load the report data into the current form
      if (report.reportData) {
        // Update the main report data
        setReportData({
          title: report.reportData.title,
          description: report.reportData.description || ''
        });
        
        // Update selected period and site
        if (report.reportData.selectedPeriod) {
          setSelectedPeriod(report.reportData.selectedPeriod);
        }
        if (report.reportData.selectedSite) {
          setSelectedSite(report.reportData.selectedSite);
        }
        
        // Load ALL report sections if they exist
        if (report.reportData.reportSections) {
          setReportSections(report.reportData.reportSections);
          console.log('✅ Loaded report sections data');
        }
        
        // Load ALL photo data if it exists
        if (report.reportData.importedPhotos) {
          setImportedPhotos(report.reportData.importedPhotos);
          console.log(`✅ Loaded ${report.reportData.importedPhotos.length} imported photos for Section 1.0`);
          console.log('   Photo data:', report.reportData.importedPhotos.slice(0, 2));
        } else {
          console.log('⚠️ No importedPhotos in report.reportData');
          setImportedPhotos([]);
        }
        
        if (report.reportData.photoLabels) {
          setPhotoLabels(report.reportData.photoLabels);
          console.log('✅ Loaded photo labels data');
        }
        
        // Switch to the Cover Page tab to show the loaded data
        setActiveTab('cover-page');
        
        // Count loaded data for user feedback
        const sectionCount = report.reportData.reportSections ? Object.keys(report.reportData.reportSections || {}).length : 0;
        const photoCount = report.reportData.importedPhotos ? report.reportData.importedPhotos.length : 0;
        
        // Update baseline state once the loaded report state has rendered
        setBaselineRequest(n => n + 1);
        
        // Show comprehensive success message (delayed to allow state updates to process)
        setTimeout(() => {
          setSuccessModal({
            isOpen: true,
            title: 'Report Loaded',
            message: `Report "${report.title}" has been loaded for editing.\n\nReport ID: ${report.id}\n\n✅ Loaded:\n- Basic report info\n- ${sectionCount} report sections\n- ${photoCount} photos with descriptions\n\nThe complete report data is now loaded in the form. When you click "Save Report", it will update this existing report.`
          });
        }, 500);
      }
    };
    
    // Function to start creating a new report (clears editing state and ALL form data)
    const handleCreateNewReport = () => {
      // Check for unsaved changes before starting new report
      const canProceed = promptForUnsavedChanges(() => {
        createNewReport();
      });
      
      if (canProceed) {
        // No unsaved changes, proceed immediately
        createNewReport();
      }
    };
    
    const createNewReport = () => {
      // Clear editing state
      setCurrentEditingReportId(null);
      
      // Reset ALL report data fields
      setReportData({ title: '', description: '' });
      
      // Set default date range (same as initial load)
      const { reportUtils } = require('./utils/reportUtils.js');
      const startDate = reportUtils.getFirstWorkingDayOfMonth();
      const endDate = reportUtils.getTodayDate();
      setSelectedPeriod({ startDate, endDate });
      
      setSelectedSite('');
      
      // Reset ALL report sections to default empty state
      const { DEFAULT_REPORT_SECTIONS } = require('./utils/reportConstants.js');
      setReportSections(DEFAULT_REPORT_SECTIONS);
      
      // Clear ALL photo data and attachments
      setImportedPhotos([]);
      setSelectedPhotos([]);
      setPhotoLabels({});
      setAvailablePhotos([]);
      
      // Reset UI state
      setActiveTab('cover-page');
      
      // Update baseline state for clean form
      updateBaselineState();
      
      console.log('✅ All report data cleared with default dates - ready for new report');
      setSuccessModal({
        isOpen: true,
        title: 'New Report Started',
        message: 'Started creating a new report. All fields and photos have been cleared, and default date range has been set. Fill in the form and click "Save Report" to create a new report entry.'
      });
    };
    
    // Duplicate report functionality
    const handleDuplicateReport = async (report) => {
      // Show confirmation modal before duplicating
      setConfirmModal({
        isOpen: true,
        title: 'Duplicate Report',
        message: `Are you sure you want to duplicate "${report.title}"?\n\nThis will create a new draft report with a fresh audit trail. Any previous publish status will not be copied.`,
        onConfirm: async () => {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
          console.log(`Creating duplicate of report: ${report.id}`);
          
          try {
            const duplicateData = {
              title: `${report.title} (Copy)`,
              site: report.site,
              reportData: {
                ...report.reportData,
                title: `${report.reportData.title} (Copy)`
              }
            };
            
            const response = await axios.post(`${API}/admin/saved-reports`, duplicateData, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            });
            
            // Add the duplicated report to the list
            setAllSavedReports(prevReports => [response.data, ...prevReports]);
            
            // Show success message
            setSuccessModal({
              isOpen: true,
              title: 'Report Duplicated',
              message: `Report duplicated successfully!\n\nNew Report: ${response.data.title}\nStatus: Draft\nAuthor: ${response.data.author}`
            });
          } catch (error) {
            console.error('Error duplicating report:', error);
            setErrorModal({
              isOpen: true,
              title: 'Error Duplicating Report',
              message: `Error duplicating report: ${error.response?.data?.detail || error.message}`
            });
          }
        }
      });
    };
    
    // Delete report functionality
    const handleDeleteReport = async (report) => {
      setConfirmModal({
        isOpen: true,
        title: 'Delete Report',
        message: `Are you sure you want to delete "${report.title}"?\n\nReport ID: ${report.id}\nCreated: ${new Date(report.dateCreated).toLocaleDateString()}\n\nThis action cannot be undone.`,
        onConfirm: async () => {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
          console.log(`Deleting report: ${report.id}`);
        
        try {
          await axios.delete(`${API}/admin/saved-reports/${report.id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          // Remove the report from the list
          setAllSavedReports(prevReports => 
            prevReports.filter(r => r.id !== report.id)
          );
          
          // Clear editing state if we're deleting the report being edited
          if (currentEditingReportId === report.id) {
            setCurrentEditingReportId(null);
          }
          
          // Show success message
          setSuccessModal({
            isOpen: true,
            title: 'Report Deleted',
            message: `Report "${report.title}" has been deleted successfully.\n\nReport ID: ${report.id}`
          });
        } catch (error) {
          console.error('Error deleting report:', error);
          setErrorModal({
            isOpen: true,
            title: 'Error Deleting Report',
            message: `Error deleting report: ${error.response?.data?.detail || error.message}`
          });
        }
        }
      });
    };

    // Modal handlers for photo selection
    const handleInsertWorkDiaryPhotos = (selectedPhotos) => {
      addUserUploadedPhotos(selectedPhotos);
      setShowWorkDiaryModal(false);
    };

    const handleInsertUploadedPhotos = (uploadedPhotos) => {
      addUserUploadedPhotos(uploadedPhotos);
      setShowUploadModal(false);
    };

    // Handle toolbar command execution
    const handleToolbarCommand = (command, value) => {
      if (toolbarCommandRef.current) {
        toolbarCommandRef.current(command, value);
      }
    };

    // Render action toolbar based on active tab
    const renderActionToolbar = () => {
      switch (activeTab) {
        case 'photos':
          // Section 1.0 - Photo Management Tools
          return (
            <>
              <button
                onClick={() => setShowWorkDiaryModal(true)}
                disabled={isGenerating}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors duration-200 border ${
                  !isGenerating
                    ? 'bg-white text-indigo-700 border-indigo-600 hover:bg-indigo-50 hover:border-indigo-700'
                    : 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                }`}
              >
                📷 Load Work Diary Photos
              </button>

              <button
                onClick={() => setShowUploadModal(true)}
                disabled={isGenerating}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors duration-200 border ${
                  !isGenerating
                    ? 'bg-white text-indigo-700 border-indigo-600 hover:bg-indigo-50 hover:border-indigo-700'
                    : 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                }`}
              >
                📁 Upload Additional Photos
              </button>
            </>
          );

        case 'executive-summary':
        case 'project-overview':
        case 'progress-highlights':
        case 'work-completed':
        case 'quality-inspections':
        case 'safety-compliance':
        case 'schedule-performance':
        case 'resource-utilization':
        case 'budget-analysis':
        case 'risk-assessment':
        case 'next-phase':
          // Section 2.0, 3.0, 4.0, 5.0, 6.0, 7.0, 8.0, 9.0, 10.0, 11.0 & 14.0 - Rich Text Toolbar
          return (
            <SharedRichTextToolbar
              isActive={isRichTextToolbarActive}
              onCommand={handleToolbarCommand}
              onAddTextbox={() => {
                if (textboxActionsRef.current?.onAdd) {
                  textboxActionsRef.current.onAdd();
                }
              }}
              onDeleteTextbox={() => {
                if (textboxActionsRef.current?.onDelete) {
                  textboxActionsRef.current.onDelete();
                }
              }}
              onAddPhotos={() => {
                if (textboxActionsRef.current?.onAddPhotos) {
                  textboxActionsRef.current.onAddPhotos();
                }
              }}
              canDeleteTextbox={focusedTextboxInfo?.canDelete || false}
              className="border-l border-gray-200 pl-3"
            />
          );

        case 'progress-comparisons':
          // Section 12.0 - Photo Grid Management
          return (
            <div className="flex items-center space-x-3 border-l border-gray-200 pl-3">
              <button
                onClick={() => {
                  if (window.progressComparisonsHandlers?.addGrid) {
                    window.progressComparisonsHandlers.addGrid();
                  }
                }}
                className="px-2 py-1 border rounded text-sm font-medium transition-colors duration-200 border-indigo-500 text-black hover:bg-indigo-50"
                title="Add Photo Grid"
              >
                ➕📷
              </button>
              
              <button
                onClick={() => {
                  console.log('Delete Photo Grid button clicked');
                  console.log('hasGridFocus:', hasGridFocus);
                  console.log('progressComparisonsHandlers:', window.progressComparisonsHandlers);
                  
                  if (window.progressComparisonsHandlers?.getFocusedGridId) {
                    const focusedGrid = window.progressComparisonsHandlers.getFocusedGridId();
                    console.log('Focused grid ID:', focusedGrid);
                    
                    if (focusedGrid && window.progressComparisonsHandlers?.deleteGrid) {
                      console.log('Calling deleteGrid with:', focusedGrid);
                      window.progressComparisonsHandlers.deleteGrid(focusedGrid);
                    } else {
                      console.log('No focused grid or deleteGrid function missing');
                    }
                  } else {
                    console.log('getFocusedGridId function not available');
                  }
                }}
                className={`px-2 py-1 border rounded text-sm font-medium transition-colors duration-200 ${
                  hasGridFocus
                    ? 'border-red-500 text-red-600 hover:bg-red-50 cursor-pointer' 
                    : 'border-gray-300 text-gray-400 cursor-not-allowed'
                }`}
                title={hasGridFocus 
                  ? "Delete Focused Photo Grid" 
                  : "Focus on a grid title or label to delete"}
                disabled={!hasGridFocus}
              >
                🗑️📷
              </button>
              
              <div className="w-px h-6 bg-gray-300 mx-1" />
              
              <button
                onClick={() => {
                  if (window.progressComparisonsHandlers?.moveGridUp) {
                    window.progressComparisonsHandlers.moveGridUp();
                  }
                }}
                className={`px-2 py-1 border rounded text-sm font-medium transition-colors duration-200 ${
                  hasGridFocus && window.progressComparisonsHandlers?.canMoveUp?.()
                    ? 'border-blue-500 text-blue-600 hover:bg-blue-50 cursor-pointer' 
                    : 'border-gray-300 text-gray-400 cursor-not-allowed'
                }`}
                title={hasGridFocus 
                  ? "Move Grid Up" 
                  : "Focus on a grid to move"}
                disabled={!hasGridFocus || !window.progressComparisonsHandlers?.canMoveUp?.()}
              >
                ↑
              </button>
              
              <button
                onClick={() => {
                  if (window.progressComparisonsHandlers?.moveGridDown) {
                    window.progressComparisonsHandlers.moveGridDown();
                  }
                }}
                className={`px-2 py-1 border rounded text-sm font-medium transition-colors duration-200 ${
                  hasGridFocus && window.progressComparisonsHandlers?.canMoveDown?.()
                    ? 'border-blue-500 text-blue-600 hover:bg-blue-50 cursor-pointer' 
                    : 'border-gray-300 text-gray-400 cursor-not-allowed'
                }`}
                title={hasGridFocus 
                  ? "Move Grid Down" 
                  : "Focus on a grid to move"}
                disabled={!hasGridFocus || !window.progressComparisonsHandlers?.canMoveDown?.()}
              >
                ↓
              </button>
            </div>
          );

        case 'technical-drawings':
          // Section 13.0 - Table Management
          return (
            <div className="flex items-center space-x-3 border-l border-gray-200 pl-3">
              <button
                onClick={() => {
                  if (window.technicalDrawingsHandlers?.addTable) {
                    window.technicalDrawingsHandlers.addTable();
                  }
                }}
                className={`px-2 py-1 border rounded text-sm font-medium transition-colors duration-200 ${
                  hasTableFocus
                    ? 'border-blue-500 text-blue-600 hover:bg-blue-50 cursor-pointer' 
                    : 'border-gray-300 text-gray-400 cursor-not-allowed'
                }`}
                title={hasTableFocus ? "Add Custom Table Below" : "Focus in a table cell to add table"}
                disabled={!hasTableFocus}
              >
                ➕📋
              </button>
              
              <button
                onClick={() => {
                  if (window.technicalDrawingsHandlers?.deleteTable) {
                    window.technicalDrawingsHandlers.deleteTable();
                  }
                }}
                className={`px-2 py-1 border rounded text-sm font-medium transition-colors duration-200 ${
                  hasTableFocus && isCustomTableFocused
                    ? 'border-red-500 text-red-600 hover:bg-red-50 cursor-pointer' 
                    : 'border-gray-300 text-gray-400 cursor-not-allowed'
                }`}
                title={hasTableFocus && isCustomTableFocused
                  ? "Delete Focused Custom Table" 
                  : "Focus on a custom table to delete"}
                disabled={!hasTableFocus || !isCustomTableFocused}
              >
                🗑️📋
              </button>
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">📊 Progress Reports</h1>
          <p className="text-blue-100">Generate comprehensive construction progress reports</p>
        </div>

        {/* Saved Reports Collapsible Section - Dark Theme */}
        <div className="bg-slate-800 rounded-xl border border-slate-700">
          {/* Collapsible Header */}
          <button
            onClick={() => setSavedReportsExpanded(!savedReportsExpanded)}
            className="w-full p-3 flex items-center justify-between hover:bg-slate-700 transition-colors duration-200 rounded-t-xl"
          >
            <div className="flex items-center space-x-3">
              <span className="text-lg">📁</span>
              <h3 className="text-base font-semibold text-white">Saved Reports</h3>
            </div>
            <span className={`transform transition-transform duration-200 text-gray-400 ${savedReportsExpanded ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>

          {/* Collapsible Content - Dark Theme */}
          {savedReportsExpanded && (
            <div className="border-t border-slate-700" style={{ maxHeight: '20vh', display: 'flex', flexDirection: 'column' }}>
              {/* Controls Row */}
              <div className="p-2 bg-slate-700/50 border-b border-slate-700 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <label className="text-xs font-medium text-gray-300">Time Period:</label>
                    <select
                      value={selectedTimePeriod}
                      onChange={(e) => setSelectedTimePeriod(e.target.value)}
                      className="px-2 py-1 bg-slate-800 border border-slate-600 rounded text-xs text-white focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="This Month">This Month</option>
                      <option value="Previous 3 Months">Previous 3 Months</option>
                      <option value="Previous 6 Months">Previous 6 Months</option>
                      <option value={`This Year (${currentYear})`}>This Year ({currentYear})</option>
                      <option value={`Last Year (${lastYear})`}>Last Year ({lastYear})</option>
                    </select>
                    <span className="text-xs text-purple-400 bg-purple-500/20 px-2 py-1 rounded border border-purple-500/30">
                      {isLoadingReports ? 'Loading...' : `${savedReports.length} report(s) found`}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* Editing message moved to report form section */}
                  </div>
                </div>
              </div>

              {/* Reports Table - Dark Theme */}
              <div className="flex-1 overflow-auto">
                <table className="w-full border-collapse text-xs">
                  <thead className="sticky top-0 bg-slate-700">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-slate-600">
                        Report Title
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-slate-600">
                        Site
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-slate-600">
                        Created
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-slate-600">
                        Last Saved
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-slate-600">
                        Last Saved By
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-slate-600">
                        Last Published
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-slate-600">
                        Status
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-slate-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingReports ? (
                      <tr>
                        <td colSpan="8" className="px-2 py-4 text-center text-gray-400">
                          <div className="flex flex-col items-center space-y-1">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
                            <p className="text-xs font-medium">Loading saved reports...</p>
                          </div>
                        </td>
                      </tr>
                    ) : savedReports.length > 0 ? (
                      savedReports.map((report, index) => (
                        <tr key={report.id} className={`hover:bg-slate-700/50 transition-colors ${index % 2 === 0 ? 'bg-slate-800' : 'bg-slate-800/50'}`}>
                          <td className="px-2 py-2 text-xs text-white border-b border-slate-700">
                            <div className="font-medium">{report.title}</div>
                          </td>
                          <td className="px-2 py-2 text-xs text-white border-b border-slate-700">
                            <div className="flex items-center">
                              <span className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                                🏗️ {getSiteName(report.site)}
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-2 text-xs text-white border-b border-slate-700">
                            <div>
                              <div className="font-medium">{new Date(report.dateCreated).toLocaleDateString('en-GB', { timeZone: 'Europe/London' })}</div>
                              <div className="text-xs text-gray-400">{new Date(report.dateCreated).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' })}</div>
                            </div>
                          </td>
                          <td className="px-2 py-2 text-xs text-white border-b border-slate-700">
                            <div>
                              <div className="font-medium">{new Date(report.lastSaved).toLocaleDateString('en-GB', { timeZone: 'Europe/London' })}</div>
                              <div className="text-xs text-gray-400">{new Date(report.lastSaved).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' })}</div>
                            </div>
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-300 border-b border-slate-700">
                            {formatAuthorName(report.author)}
                          </td>
                          <td className="px-2 py-2 text-xs text-white border-b border-slate-700">
                            {report.published_at ? (
                              <div>
                                <div className="font-medium">{new Date(report.published_at).toLocaleDateString('en-GB', { timeZone: 'Europe/London' })}</div>
                                <div className="text-xs text-gray-400">{new Date(report.published_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' })}</div>
                              </div>
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-xs border-b border-slate-700">
                            <span className={`inline-flex px-1 py-0.5 text-xs font-semibold rounded ${
                              report.status === 'Published' 
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                                : report.status === 'Draft'
                                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                : 'bg-slate-600 text-gray-300'
                            }`}>
                              {report.status}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-xs border-b border-slate-700">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditReport(report)}
                                className="px-2 py-1 text-xs font-medium text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 border border-blue-500/30 rounded transition-colors duration-200"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDuplicateReport(report)}
                                className="px-2 py-1 text-xs font-medium text-green-400 hover:text-green-300 hover:bg-green-500/20 border border-green-500/30 rounded transition-colors duration-200"
                              >
                                Duplicate
                              </button>
                              <button
                                onClick={async () => {
                                  if (report.status === 'Published' && report.has_published_pdf) {
                                    try {
                                      setDownloadingReportId(report.id);
                                      const token = localStorage.getItem('token');
                                      const response = await axios.get(
                                        `${API}/admin/saved-reports/${report.id}/download`,
                                        {
                                          headers: { 'Authorization': `Bearer ${token}` },
                                          responseType: 'blob'
                                        }
                                      );
                                      // Create download link
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.setAttribute('download', `${report.title.replace(/\s+/g, '_')}.pdf`);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      window.URL.revokeObjectURL(url);
                                    } catch (error) {
                                      console.error('Download error:', error);
                                      setErrorModal({
                                        isOpen: true,
                                        title: 'Download Failed',
                                        message: 'Failed to download the report. Please try again.'
                                      });
                                    } finally {
                                      setDownloadingReportId(null);
                                    }
                                  }
                                }}
                                disabled={report.status !== 'Published' || !report.has_published_pdf || downloadingReportId === report.id}
                                className={`px-2 py-1 text-xs font-medium rounded transition-all duration-200 ${
                                  downloadingReportId === report.id
                                    ? 'text-cyan-300 bg-cyan-500/30 border border-cyan-400 animate-pulse'
                                    : report.status === 'Published' && report.has_published_pdf
                                      ? 'text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20 border border-cyan-500/30 active:scale-95 active:bg-cyan-500/40'
                                      : 'text-gray-500 border border-gray-600 cursor-not-allowed opacity-50'
                                }`}
                                title={report.status !== 'Published' ? 'Publish the report to enable download' : ''}
                              >
                                {downloadingReportId === report.id ? (
                                  <span className="flex items-center space-x-1">
                                    <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Downloading...</span>
                                  </span>
                                ) : 'Download'}
                              </button>
                              <button
                                onClick={() => setAuditTrailModal({ isOpen: true, reportId: report.id, reportTitle: report.title })}
                                className="px-2 py-1 text-xs font-medium text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/20 border border-indigo-500/30 rounded transition-colors duration-200"
                              >
                                History
                              </button>
                              <button
                                onClick={() => handleDeleteReport(report)}
                                className="px-2 py-1 text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-500/20 border border-red-500/30 rounded transition-colors duration-200"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-2 py-4 text-center text-gray-400">
                          <div className="flex flex-col items-center space-y-1">
                            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="text-xs font-medium text-gray-300">No saved reports found</p>
                            <p className="text-xs text-gray-500">
                              {selectedTimePeriod === 'This Month' ? 'No reports saved this month' :
                               selectedTimePeriod === 'Previous 3 Months' ? 'No reports saved in the previous 3 months' :
                               selectedTimePeriod === 'Previous 6 Months' ? 'No reports saved in the previous 6 months' :
                               selectedTimePeriod.includes('This Year') ? `No reports saved this year (${currentYear})` :
                               selectedTimePeriod.includes('Last Year') ? `No reports saved last year (${lastYear})` :
                               'Try selecting a different time period to find saved reports'}
                            </p>
                            <button
                              onClick={handleSaveCurrentReport}
                              className="mt-2 px-3 py-1 bg-purple-500/20 text-purple-400 rounded text-xs hover:bg-purple-500/30 transition-colors border border-purple-500/30"
                            >
                              Save Current Report
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          {/* Split Button Layout: Persistent Actions (Left) + Action Toolbar (Right) */}
          <div className="mb-6">
            <div className="flex items-center justify-between flex-wrap gap-y-2">
              {/* Persistent Buttons - Always Visible (Left Side) */}
              <div className="flex items-center space-x-3 flex-wrap gap-y-2">
                <button
                  onClick={handleCreateNewReport}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 border bg-slate-700 text-purple-400 border-purple-500/50 hover:bg-purple-500/20 hover:border-purple-400"
                >
                  📝 New Report
                </button>
              
                <button
                  onClick={handleSaveCurrentReport}
                  disabled={!reportData.title || !selectedSite || !selectedPeriod.startDate || !selectedPeriod.endDate}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 border ${
                    (reportData.title && selectedSite && selectedPeriod.startDate && selectedPeriod.endDate)
                      ? 'bg-slate-700 text-purple-400 border-purple-500/50 hover:bg-purple-500/20 hover:border-purple-400' 
                      : 'bg-slate-700/50 text-gray-500 border-slate-600 cursor-not-allowed'
                  }`}
                >
                  {currentEditingReportId ? 
                    (reportData.title && selectedSite && selectedPeriod.startDate && selectedPeriod.endDate ? '💿 Update Report' : '💿 Complete Fields to Update') :
                    (reportData.title && selectedSite && selectedPeriod.startDate && selectedPeriod.endDate ? '💿 Save New Report' : '💿 Complete Fields to Save')
                  }
                </button>
                
                {/* Load Sample Data Button - Active functionality */}
                <button
                  onClick={populateReportWithSampleData}
                  disabled={isGenerating}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center ${
                    isGenerating
                      ? 'bg-slate-700 text-gray-500 cursor-not-allowed' 
                      : 'bg-slate-600 text-gray-200 border border-slate-500 hover:bg-slate-500 hover:text-white'
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <svg className="w-4 h-4 mr-2 animate-spin" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                      </svg>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Load Sample Data
                    </>
                  )}
                </button>

                {/* AI Report Generator Button */}
                <div className="relative group" onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltipPos({
                    left: rect.right + 16,
                    top: rect.top
                  });
                }}>
                  <button
                    onClick={() => setShowAIReportModal(true)}
                    disabled={subscriptionFeatures && !subscriptionFeatures.aiReportGeneration}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center ${
                      subscriptionFeatures && !subscriptionFeatures.aiReportGeneration
                        ? 'bg-slate-700 text-gray-500 cursor-not-allowed opacity-75'
                        : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-500 hover:to-indigo-500 shadow-lg shadow-purple-500/25'
                    }`}
                    data-testid="ai-report-generator-btn"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                    </svg>
                    AI Report Generator
                    {subscriptionFeatures && !subscriptionFeatures.aiReportGeneration && (
                      <svg className="w-3 h-3 text-amber-400 ml-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                  {/* Tooltip for AI Report Generator - only show when disabled */}
                  {subscriptionFeatures && !subscriptionFeatures.aiReportGeneration && (
                    <div className="pointer-events-none fixed opacity-0 group-hover:opacity-100 transition-all duration-200 z-[10000]" style={{left: `${tooltipPos.left}px`, top: `${tooltipPos.top}px`}}>
                      <div className="relative bg-gradient-to-br from-slate-700 to-slate-800 text-white text-xs rounded-lg p-4 shadow-2xl border border-slate-600 min-w-[280px]">
                        <div className="absolute right-full top-4 border-8 border-transparent border-r-slate-700"></div>
                        <div className="flex items-start mb-2">
                          <svg className="w-5 h-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                          </svg>
                          <div>
                            <p className="font-bold text-sm text-white mb-1">AI Report Generator</p>
                            <p className="text-gray-300 leading-relaxed">
                              This feature uses AI to automatically generate report content based on your project data.
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-600">
                          <p className="text-amber-400 text-xs font-medium">
                            🔒 Upgrade your subscription to unlock this feature
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>


                <button
                  onClick={() => setShowReportGenerationModal(true)}
                  disabled={isGenerating || !checkRequiredFields()}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 border ${
                    checkRequiredFields()
                      ? 'bg-slate-700 text-purple-400 border-purple-500/50 hover:bg-purple-500/20 hover:border-purple-400'
                      : 'bg-slate-700/50 text-gray-500 border-slate-600 cursor-not-allowed'
                  }`}
                >
                  {isGenerating ? '⟳ Generating...' : '📄 Preview Report'}
                </button>
              </div>

              {/* Action Toolbar - Tab-Specific Tools (Right Side) */}
              <div className="flex items-center space-x-3 flex-wrap gap-y-2 px-4 py-2 bg-slate-700/50 rounded-lg border border-slate-600">
                {renderActionToolbar()}
              </div>
            </div>
              
            {/* Dynamic editing status - below buttons */}
            {currentEditingReportId && (
              <div className="flex items-center mt-3 pt-3 border-t border-slate-700">
                <span className={`text-xs px-2 py-1 rounded ${
                  hasUnsavedChanges 
                    ? 'text-red-400 bg-red-500/20 border border-red-500/30' 
                    : 'text-gray-400'
                }`}>
                  <span className="font-medium">Editing:</span> {allSavedReports.find(report => report.id === currentEditingReportId)?.title || currentEditingReportId}
                  {hasUnsavedChanges && <span className="ml-1 font-medium">*</span>}
                </span>
              </div>
            )}
          </div>

          <div className="flex gap-6 h-screen overflow-hidden">
            {/* Tab Navigation - Dark Theme Sidebar */}
            <div className="w-80 flex-shrink-0 overflow-y-auto bg-slate-800 rounded-xl border border-slate-700 p-3" style={{ maxHeight: '95vh' }}>
              <SectionPresetBar />
              <div className="space-y-6 pr-2">
                {REPORT_CATEGORIES.map(category => (
                  <div key={category.id} className="space-y-2">
                    <div className="flex items-center space-x-2 px-3 py-2 bg-slate-700/50 rounded-lg border border-slate-600">
                      <span className="text-lg">{category.icon}</span>
                      <span className="font-medium text-white">{category.name}</span>
                    </div>
                    <div className="space-y-1 ml-4">
                      {REPORT_TABS.filter(tab => tab.category === category.id).map(tab => (
                        <div
                          key={tab.id}
                          role="button"
                          tabIndex={0}
                          data-testid={`report-tab-${tab.id}`}
                          onClick={() => setActiveTab(tab.id)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab(tab.id); } }}
                          className={`w-full text-left p-3 rounded-lg border cursor-pointer transition-colors duration-200 ${
                            activeTab === tab.id
                              ? 'border-purple-500 bg-purple-500/20 text-white shadow-sm'
                              : 'border-slate-600 hover:border-slate-500 hover:bg-slate-700/50 text-gray-300'
                          } ${isSectionIncluded(tab.id) ? '' : 'opacity-50'}`}
                        >
                          <div className="flex items-start space-x-3">
                            <span className="text-lg">{tab.icon}</span>
                            <div className="min-w-0 flex-1">
                              <div className={`font-medium text-xs ${activeTab === tab.id ? 'text-white' : 'text-gray-200'}`}>{tab.name}</div>
                              <div className={`text-xs mt-1 ${activeTab === tab.id ? 'text-purple-200' : 'text-gray-400'}`}>{tab.description}</div>
                              {!isSectionIncluded(tab.id) && (
                                <span data-testid={`report-tab-excluded-tag-${tab.id}`} className="inline-block mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-600 text-gray-200">Excluded</span>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <SectionIncludeSwitch tabId={tab.id} />
                              {getCompletionIndicator(getTabCompletionStatus(tab.id))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tab Content - Independent Scrollbar */}
            {/* All tabs are mounted but only active one is visible */}
            <div className="flex-1 min-w-0 overflow-y-auto" style={{ maxHeight: '95vh' }}>
              <div className="pr-2">
                <ExcludedSectionBanner />
                <div style={{ display: activeTab === 'cover-page' ? 'block' : 'none' }}>
                  <CoverPageTab />
                </div>
                <div style={{ display: activeTab === 'contents' ? 'block' : 'none' }}>
                  <ContentsTab />
                </div>
                <div style={{ display: activeTab === 'photos' ? 'block' : 'none' }}>
                  <PhotoSelectionTab />
                </div>
                <div style={{ display: activeTab === 'executive-summary' ? 'block' : 'none' }}>
                  <ExecutiveSummaryTab 
                    toolbarCommandRef={toolbarCommandRef}
                    textboxActionsRef={textboxActionsRef}
                    onRichTextFocusChange={setIsRichTextToolbarActive}
                    onTextboxInfoChange={setFocusedTextboxInfo}
                  />
                </div>
                <div style={{ display: activeTab === 'project-overview' ? 'block' : 'none' }}>
                  <ProjectOverviewTab 
                    toolbarCommandRef={toolbarCommandRef}
                    textboxActionsRef={textboxActionsRef}
                    onRichTextFocusChange={setIsRichTextToolbarActive}
                    onTextboxInfoChange={setFocusedTextboxInfo}
                  />
                </div>
                <div style={{ display: activeTab === 'progress-highlights' ? 'block' : 'none' }}>
                  <ProgressHighlightsTab 
                    toolbarCommandRef={toolbarCommandRef}
                    textboxActionsRef={textboxActionsRef}
                    onRichTextFocusChange={setIsRichTextToolbarActive}
                    onTextboxInfoChange={setFocusedTextboxInfo}
                  />
                </div>
                <div style={{ display: activeTab === 'work-completed' ? 'block' : 'none' }}>
                  <WorkCompletedTab 
                    toolbarCommandRef={toolbarCommandRef}
                    textboxActionsRef={textboxActionsRef}
                    onRichTextFocusChange={setIsRichTextToolbarActive}
                    onTextboxInfoChange={setFocusedTextboxInfo}
                  />
                </div>
                <div style={{ display: activeTab === 'quality-inspections' ? 'block' : 'none' }}>
                  <QualityInspectionsTab 
                    toolbarCommandRef={toolbarCommandRef}
                    textboxActionsRef={textboxActionsRef}
                    onRichTextFocusChange={setIsRichTextToolbarActive}
                    onTextboxInfoChange={setFocusedTextboxInfo}
                  />
                </div>
                <div style={{ display: activeTab === 'safety-compliance' ? 'block' : 'none' }}>
                  <SafetyComplianceTab 
                    toolbarCommandRef={toolbarCommandRef}
                    textboxActionsRef={textboxActionsRef}
                    onRichTextFocusChange={setIsRichTextToolbarActive}
                    onTextboxInfoChange={setFocusedTextboxInfo}
                  />
                </div>
                <div style={{ display: activeTab === 'schedule-performance' ? 'block' : 'none' }}>
                  <SchedulePerformanceTab 
                    toolbarCommandRef={toolbarCommandRef}
                    textboxActionsRef={textboxActionsRef}
                    onRichTextFocusChange={setIsRichTextToolbarActive}
                    onTextboxInfoChange={setFocusedTextboxInfo}
                  />
                </div>
                <div style={{ display: activeTab === 'resource-utilization' ? 'block' : 'none' }}>
                  <ResourceUtilizationTab 
                    toolbarCommandRef={toolbarCommandRef}
                    textboxActionsRef={textboxActionsRef}
                    onRichTextFocusChange={setIsRichTextToolbarActive}
                    onTextboxInfoChange={setFocusedTextboxInfo}
                  />
                </div>
                <div style={{ display: activeTab === 'budget-analysis' ? 'block' : 'none' }}>
                  <BudgetAnalysisTab 
                    toolbarCommandRef={toolbarCommandRef}
                    textboxActionsRef={textboxActionsRef}
                    onRichTextFocusChange={setIsRichTextToolbarActive}
                    onTextboxInfoChange={setFocusedTextboxInfo}
                  />
                </div>
                <div style={{ display: activeTab === 'risk-assessment' ? 'block' : 'none' }}>
                  <RiskAssessmentTab 
                    toolbarCommandRef={toolbarCommandRef}
                    textboxActionsRef={textboxActionsRef}
                    onRichTextFocusChange={setIsRichTextToolbarActive}
                    onTextboxInfoChange={setFocusedTextboxInfo}
                  />
                </div>
                <div style={{ display: activeTab === 'progress-comparisons' ? 'block' : 'none' }}>
                  <ProgressComparisonsTab />
                </div>
                <div style={{ display: activeTab === 'next-phase' ? 'block' : 'none' }}>
                  <NextPhaseTab 
                    toolbarCommandRef={toolbarCommandRef}
                    textboxActionsRef={textboxActionsRef}
                    onRichTextFocusChange={setIsRichTextToolbarActive}
                    onTextboxInfoChange={setFocusedTextboxInfo}
                  />
                </div>
                <div style={{ display: activeTab === 'technical-drawings' ? 'block' : 'none' }}>
                  <TechnicalDrawingsTab />
                </div>
                <div style={{ display: activeTab === 'appendices' ? 'block' : 'none' }}>
                  <AppendicesTab />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Photo Selection Modals */}
        {showWorkDiaryModal && (
          <WorkDiaryPhotosModal
            isOpen={showWorkDiaryModal}
            onClose={() => setShowWorkDiaryModal(false)}
            onInsert={handleInsertWorkDiaryPhotos}
            selectedPeriod={selectedPeriod}
            selectedSite={selectedSite}
          />
        )}

        {showUploadModal && (
          <UploadPhotosModal
            isOpen={showUploadModal}
            onClose={() => setShowUploadModal(false)}
            onInsert={handleInsertUploadedPhotos}
          />
        )}

        {/* Report Generation Modal */}
        {showReportGenerationModal && (
          <ReportGenerationModal
            isOpen={showReportGenerationModal}
            onClose={() => setShowReportGenerationModal(false)}
            currentEditingReportId={currentEditingReportId}
            hasUnsavedChanges={hasUnsavedChanges}
            currentReportStatus={allSavedReports.find(r => r.id === currentEditingReportId)?.status}
            hasPublishedPdf={allSavedReports.find(r => r.id === currentEditingReportId)?.has_published_pdf}
            onPublishSuccess={(publishData) => {
              // Update the saved reports list with new status
              setAllSavedReports(prev => prev.map(report => 
                report.id === currentEditingReportId 
                  ? { ...report, status: 'Published', has_published_pdf: true, published_at: publishData.published_at }
                  : report
              ));
              setSuccessModal({
                isOpen: true,
                title: 'Report Published',
                message: 'Your report has been published successfully and is now available for download.'
              });
            }}
          />
        )}

        {/* AI Report Generator Modal */}
        {showAIReportModal && (
          <AIReportGeneratorModal
            isOpen={showAIReportModal}
            onClose={() => setShowAIReportModal(false)}
            sites={sites}
            onGenerate={async (aiReportData) => {
              try {
                // Show loading state
                setSuccessModal({
                  isOpen: true,
                  title: 'Generating Report...',
                  message: 'AI is analyzing your site data and generating report content. This may take a moment...'
                });
                
                const token = localStorage.getItem('token');
                
                // Call AI report generation endpoint
                const response = await axios.post(
                  `${API}/admin/reports/generate-ai-content`,
                  {
                    site_id: aiReportData.siteId,
                    start_date: aiReportData.startDate,
                    end_date: aiReportData.endDate,
                    report_title: aiReportData.reportTitle,
                    programme_status: aiReportData.programmeStatus,
                    quality_inspections: aiReportData.qualityInspections,
                    safety_incidents: aiReportData.safetyIncidents,
                    budget_status: aiReportData.budgetStatus,
                    progress_photos_to_show: aiReportData.progressPhotosToShow || null,
                    before_after_photos_to_show: aiReportData.beforeAfterPhotosToShow || null,
                    import_technical_drawings: aiReportData.importTechnicalDrawings || false,
                    generate_placeholders: true
                  },
                  {
                    headers: { Authorization: `Bearer ${token}` }
                  }
                );
                
                if (response.data.success) {
                  const { sections, metadata, credits_charged, credits_remaining } = response.data;
                  
                  // Set basic report data
                  setSelectedSite(aiReportData.siteId);
                  setSelectedPeriod({
                    startDate: aiReportData.startDate,
                    endDate: aiReportData.endDate
                  });
                  
                  setReportData(prev => ({
                    ...prev,
                    title: aiReportData.reportTitle,
                    projectName: aiReportData.siteName,
                    selectedSite: aiReportData.siteId
                  }));
                  
                  // Update all report sections with AI-generated content
                  setReportSections(prev => ({
                    ...prev,
                    coverPage: {
                      ...prev.coverPage,
                      projectTitle: aiReportData.reportTitle
                    },
                    executiveSummary: {
                      overview: sections.executiveSummary || '',
                      keyMetrics: '',
                      majorAchievements: '',
                      challenges: '',
                      recommendations: ''
                    },
                    projectOverview: {
                      projectDescription: sections.projectOverview || '',
                      scope: '',
                      objectives: '',
                      timeline: '',
                      stakeholders: ''
                    },
                    progressHighlights: {
                      completedMilestones: sections.progressHighlights || '',
                      currentActivities: '',
                      upcomingWork: '',
                      percentComplete: ''
                    },
                    workCompleted: {
                      activities: sections.workCompleted || '',
                      deliverables: '',
                      manHours: '',
                      materials: '',
                      equipment: ''
                    },
                    qualityInspections: {
                      inspections: sections.qualityInspections || '',
                      standards: '',
                      compliance: '',
                      defects: '',
                      corrective: ''
                    },
                    safetyCompliance: {
                      incidents: sections.safetyCompliance || '',
                      measures: '',
                      training: '',
                      compliance: '',
                      improvements: ''
                    },
                    schedulePerformance: {
                      planned: sections.schedulePerformance || '',
                      actual: '',
                      variance: '',
                      delays: '',
                      recovery: ''
                    },
                    resourceUtilization: {
                      labor: sections.resourceUtilization || '',
                      equipment: '',
                      materials: '',
                      efficiency: '',
                      optimization: ''
                    },
                    budgetAnalysis: {
                      approved: sections.budgetAnalysis || '',
                      committed: '',
                      actual: '',
                      variance: '',
                      forecast: ''
                    },
                    riskAssessment: {
                      identified: sections.riskAssessment || '',
                      probability: '',
                      impact: '',
                      mitigation: '',
                      contingency: ''
                    },
                    progressComparisons: {
                      before: '',
                      after: sections.progressComparisons || '',
                      analysis: '',
                      improvements: ''
                    },
                    technicalDrawings: sections.technicalDrawings || {
                      drawings: '',
                      revisions: '',
                      approvals: '',
                      specifications: ''
                    },
                    nextPhase: {
                      activities: sections.nextPhase || '',
                      timeline: '',
                      resources: '',
                      dependencies: '',
                      risks: ''
                    },
                    appendices: {
                      documents: '',
                      references: '',
                      calculations: '',
                      certifications: ''
                    }
                  }));
                  
                  // Handle imported photos if available
                  if (sections._photos) {
                    const progressPhotos = sections._photos.progress_photos || [];
                    if (progressPhotos.length > 0) {
                      setImportedPhotos(progressPhotos.map(p => ({
                        id: p.id,
                        url: p.url,
                        description: p.description || '',
                        date: p.upload_date,
                        siteName: p.site_name
                      })));
                    }
                  }
                  
                  // Show success message
                  const statusSummary = [
                    `Programme: ${aiReportData.programmeStatus.replace('-', ' ')}`,
                    `Quality Inspections: ${aiReportData.qualityInspections}`,
                    `Safety Incidents: ${aiReportData.safetyIncidents}`,
                    `Budget: ${aiReportData.budgetStatus.replace('-', ' ')}`
                  ].join(' | ');
                  
                  setSuccessModal({
                    isOpen: true,
                    title: 'Report Generated Successfully',
                    message: `AI has generated content for "${aiReportData.reportTitle}".\n\n` +
                      `📊 Data Analyzed:\n` +
                      `• Tasks: ${metadata.tasks_analyzed || 0}\n` +
                      `• Photos: ${metadata.photos_included || 0}\n` +
                      `• Attendance Records: ${metadata.attendance_records || 0}\n\n` +
                      `💳 Credits: ${credits_charged} used (${credits_remaining} remaining)\n\n` +
                      `Status: ${statusSummary}\n\n` +
                      `You can now review and edit the generated content in each section.`
                  });
                  
                  console.log('AI Report Generated:', { sections, metadata });
                }
              } catch (error) {
                console.error('AI Report generation error:', error);
                
                const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate report';
                
                setErrorModal({
                  isOpen: true,
                  title: 'Report Generation Failed',
                  message: errorMessage
                });
              }
            }}
          />
        )}

        {/* Custom Modals for Report Operations */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText="Confirm"
          cancelText="Cancel"
          variant="danger"
        />

        <SuccessModal
          isOpen={successModal.isOpen}
          onClose={() => setSuccessModal({ isOpen: false, title: '', message: '' })}
          title={successModal.title}
          message={successModal.message}
        />

        <ErrorModal
          isOpen={errorModal.isOpen}
          onClose={() => setErrorModal({ isOpen: false, title: '', message: '' })}
          title={errorModal.title}
          message={errorModal.message}
        />
      </div>
    );
  };

  // Render other report types (placeholder for now)
  const renderOtherReportTypes = () => {
    const currentReportType = REPORT_TYPES.find(type => type.id === activeReportType);
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-bold text-gray-900">
            {currentReportType?.icon} {currentReportType?.name}
          </h2>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="text-6xl mb-4">{currentReportType?.icon}</div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">{currentReportType?.name}</h3>
          <p className="text-gray-600 mb-8">{currentReportType?.description}</p>
          <div className="text-gray-500">
            <p className="mb-4">This report type is coming soon!</p>
            <p className="text-sm">We're working on implementing advanced functionality for this report type.</p>
          </div>
        </div>
      </div>
    );
  };

  // Main render logic
  if (!activeReportType) {
    return renderReportTypeSelection();
  } else if (activeReportType === 'monthly-progress') {
    return (
      <>
        {renderProgressReportsTabInterface()}
        {auditTrailModal.isOpen && (
          <AuditTrailModal
            reportId={auditTrailModal.reportId}
            reportTitle={auditTrailModal.reportTitle}
            onClose={() => setAuditTrailModal({ isOpen: false, reportId: null, reportTitle: '' })}
          />
        )}
        
        {/* Navigation Blocker Modal */}
        {showNavigationBlocker && (
          <div 
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
            onClick={(e) => {
              // Backdrop click disabled - modal must be closed via buttons
            }}
          >
            <div 
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all"
              style={{ animation: 'scaleIn 0.2s ease-out' }}
            >
              <div className="p-6">
                {/* Icon */}
                <div className="flex justify-center mb-4">
                  <div className="rounded-full p-3 bg-red-100 dark:bg-red-900">
                    <svg className="w-12 h-12 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-center text-gray-900 dark:text-white mb-2">
                  Unsaved Changes
                </h3>

                {/* Message */}
                <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
                  You have unsaved changes that will be lost if you leave this page. Are you sure you want to continue?
                </p>

                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowNavigationBlocker(false);
                      pendingNavigationRef.current = null;
                    }}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                  >
                    Stay on Page
                  </button>
                  <button
                    onClick={() => {
                      const targetPath = pendingNavigationRef.current;
                      setShowNavigationBlocker(false);
                      setHasUnsavedChangesForNav(false);
                      pendingNavigationRef.current = null;
                      if (targetPath) {
                        navigateForBlocker(targetPath);
                      }
                    }}
                    className="flex-1 px-4 py-3 rounded-lg text-white font-semibold bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                  >
                    Leave Without Saving
                  </button>
                </div>
              </div>
            </div>

            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes scaleIn {
                from { opacity: 0; transform: scale(0.95); }
                to { opacity: 1; transform: scale(1); }
              }
            `}</style>
          </div>
        )}
      </>
    );
  } else {
    return renderOtherReportTypes();
  }
};

const Reports = ({ defaultReportType = '' }) => {
  return (
    <ReportsProvider defaultReportType={defaultReportType}>
      <ReportsContent />
    </ReportsProvider>
  );
};

export default Reports;